import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dumbbell, LogIn, LogOut, User, MapPin, BellRing } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n, LangSwitch } from "@/lib/i18n";

const roleLabel = {
  admin: "Admin",
  room_manager: "Responsable Salle",
  coach: "Coach",
  client: "Particulier",
};
const roleClass = {
  admin: "bg-slate-900 text-white",
  room_manager: "bg-indigo-500/10 text-indigo-700 border border-indigo-500/20",
  coach: "bg-orange-500/10 text-orange-700 border border-orange-500/20",
  client: "bg-emerald-500/10 text-emerald-700 border border-emerald-500/20",
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [openNotif, setOpenNotif] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
      const t = setInterval(() => {
        api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
      }, 15000);
      return () => clearInterval(t);
    }
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;

  const link = ({ isActive }) =>
    `px-3 py-2 text-sm rounded-full transition ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`;

  return (
    <header className="sc-glass sticky top-0 z-40">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 font-extrabold text-slate-900">
          <span className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center">
            <Dumbbell className="w-5 h-5" />
          </span>
          <span className="text-lg tracking-tight">TymCoach</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink data-testid="nav-map" to="/carte" className={link}><MapPin className="inline w-4 h-4 mr-1" />{t("Carte & Salles")}</NavLink>
          <NavLink data-testid="nav-coaches" to="/coachs" className={link}>{t("Coachs")}</NavLink>
          <NavLink data-testid="nav-howitworks" to="/comment-ca-marche" className={link}>{t("Comment ça marche")}</NavLink>
          <LangSwitch />
        </nav>

        <div className="flex items-center gap-2">
          {user && user.id && (
            <div className="relative">
              <button
                data-testid="notifications-btn"
                onClick={() => setOpenNotif((o) => !o)}
                className="relative p-2 rounded-full hover:bg-slate-100"
                aria-label="Notifications"
              >
                <BellRing className="w-5 h-5" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px] rounded-full bg-orange-500 text-white grid place-items-center">
                    {unread}
                  </span>
                )}
              </button>
              {openNotif && (
                <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-50">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-2 py-1">Notifications</div>
                  {notifs.length === 0 && <div className="p-3 text-sm text-slate-500">Aucune notification.</div>}
                  {notifs.slice(0, 6).map((n) => (
                    <div key={n.id} data-testid={`notif-${n.id}`} className={`px-3 py-2 rounded-lg text-sm ${n.read ? "text-slate-500" : "bg-slate-50 text-slate-800"}`}>
                      <div className="font-semibold">{n.title}</div>
                      <div className="text-xs">{n.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {user && user.id ? (
            <>
              <span className={`hidden sm:inline text-xs font-semibold px-2.5 py-1 rounded-full ${roleClass[user.role]}`} data-testid="user-role-badge">
                {roleLabel[user.role]}
              </span>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="sc-btn sc-btn-primary">Admin</Link>
              )}
              <Link to="/tableau-de-bord" data-testid="nav-dashboard" className="sc-btn sc-btn-outline">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Moi"}
              </Link>
              <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="sc-btn sc-btn-outline">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : user === false ? (
            <>
              <Link to="/connexion" data-testid="nav-login" className="sc-btn sc-btn-outline"><LogIn className="w-4 h-4" /> {t("Connexion")}</Link>
              <Link to="/inscription" data-testid="nav-register" className="sc-btn sc-btn-primary">{t("Inscription")}</Link>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
